/**
 * useContactDetail Hook
 *
 * Manages contact detail data fetching and state.
 * Preloads related data (insights, knowledge, communication) in background.
 */

import { useState, useEffect, useCallback } from 'react';
import { contactsApi } from '@/services/contactsApi';
import type {
  Contact,
  ContactEmailInsights,
  KnowledgeCard,
  CommunicationResponse,
} from '../types';

interface SuppressedItem {
  item_type: string;
  item_value: string;
}

interface UseContactDetailResult {
  contact: Contact | null;
  loading: boolean;
  error: string | null;
  insights: ContactEmailInsights | null;
  insightsLoading: boolean;
  knowledge: KnowledgeCard[];
  knowledgeLoading: boolean;
  communication: CommunicationResponse | null;
  communicationLoading: boolean;
  suppressedItems: SuppressedItem[];
  refresh: () => Promise<void>;
  refreshInsights: () => Promise<void>;
  refreshKnowledge: () => Promise<void>;
  refreshCommunication: () => Promise<void>;
  setContact: React.Dispatch<React.SetStateAction<Contact | null>>;
  setKnowledge: React.Dispatch<React.SetStateAction<KnowledgeCard[]>>;
}

export function useContactDetail(contactId: string | undefined): UseContactDetailResult {
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [insights, setInsights] = useState<ContactEmailInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const [knowledge, setKnowledge] = useState<KnowledgeCard[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);

  const [communication, setCommunication] = useState<CommunicationResponse | null>(null);
  const [communicationLoading, setCommunicationLoading] = useState(false);


  // Load contact
  const refresh = useCallback(async () => {
    if (!contactId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await contactsApi.get(contactId);
      setContact(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contact');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  // Load email insights
  const refreshInsights = useCallback(async () => {
    if (!contactId) return;

    setInsightsLoading(true);
    try {
      const data = await contactsApi.getEmailInsights(contactId);
      setInsights(data);
    } catch (err) {
      console.error('Failed to load email insights:', err);
      setInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  }, [contactId]);

  // Load knowledge cards
  const refreshKnowledge = useCallback(async () => {
    if (!contactId) return;

    setKnowledgeLoading(true);
    try {
      const data = await contactsApi.getKnowledgeCards(contactId);
      setKnowledge(data);
    } catch (err) {
      console.error('Failed to load knowledge cards:', err);
      setKnowledge([]);
    } finally {
      setKnowledgeLoading(false);
    }
  }, [contactId]);

  // Load communication (email threads)
  const refreshCommunication = useCallback(async () => {
    if (!contactId) return;

    setCommunicationLoading(true);
    try {
      const data = await contactsApi.getCommunication(contactId);
      setCommunication(data);
    } catch (err) {
      console.error('Failed to load communication:', err);
      setCommunication(null);
    } finally {
      setCommunicationLoading(false);
    }
  }, [contactId]);

  // Load contact on mount/contactId change
  useEffect(() => {
    if (!contactId) {
      setContact(null);
      setLoading(false);
      return;
    }

    // Reset state
    setInsights(null);
    setKnowledge([]);
    setCommunication(null);

    // Load contact
    refresh();

    // Background preload other data (non-blocking)
    contactsApi.getEmailInsights(contactId)
      .then(setInsights)
      .catch(() => setInsights(null));

    contactsApi.getKnowledgeCards(contactId)
      .then(setKnowledge)
      .catch(() => setKnowledge([]));

    contactsApi.getCommunication(contactId)
      .then(setCommunication)
      .catch(() => setCommunication(null));

  }, [contactId, refresh]);

  // Derive suppressed items from knowledge cards
  const suppressedItems = knowledge
    .filter(c => c.card_type === 'suppression')
    .map(card => {
      try {
        const data = JSON.parse(card.content);
        return {
          item_type: data.suppressed_type || '',
          item_value: data.suppressed_content || '',
        };
      } catch {
        return { item_type: '', item_value: '' };
      }
    })
    .filter(item => item.item_type && item.item_value);

  return {
    contact,
    loading,
    error,
    insights,
    insightsLoading,
    knowledge,
    knowledgeLoading,
    communication,
    communicationLoading,
    suppressedItems,
    refresh,
    refreshInsights,
    refreshKnowledge,
    refreshCommunication,
    setContact,
    setKnowledge,
  };
}
